import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import {
  ArrowLeft, Tag, Package, Edit, AlertCircle, X, Check, Search,
  ChevronRight, AlertTriangle, Wrench, Laptop, Bed, Utensils, Lightbulb,
  Tent, Car, Droplet, Flame, Leaf, Music, Camera, Gamepad2, Bike, Waves
} from 'lucide-react';

// ============================================================================
// CONFIGURATIONS
// ============================================================================

const statusConfig: Record<string, { label: string; bg: string; text: string; gradient: string }> = {
  active: { label: 'Attivo', bg: 'bg-green-100', text: 'text-green-800', gradient: 'from-green-500 to-emerald-600' },
  in_repair: { label: 'In Riparazione', bg: 'bg-orange-100', text: 'text-orange-800', gradient: 'from-orange-500 to-amber-600' },
  in_maintenance: { label: 'In Manutenzione', bg: 'bg-blue-100', text: 'text-blue-800', gradient: 'from-blue-500 to-indigo-600' },
  in_transit: { label: 'In Transito', bg: 'bg-purple-100', text: 'text-purple-800', gradient: 'from-purple-500 to-violet-600' },
  in_storage: { label: 'In Magazzino', bg: 'bg-gray-100', text: 'text-gray-800', gradient: 'from-gray-500 to-slate-600' },
  disposed: { label: 'Dismesso', bg: 'bg-red-100', text: 'text-red-800', gradient: 'from-red-500 to-rose-600' },
  lost: { label: 'Perso', bg: 'bg-red-100', text: 'text-red-800', gradient: 'from-red-500 to-rose-600' }
};

const conditionConfig: Record<string, { label: string; color: string; emoji: string }> = {
  excellent: { label: 'Eccellente', color: 'text-green-600', emoji: '✨' },
  good: { label: 'Buono', color: 'text-blue-600', emoji: '👍' },
  fair: { label: 'Discreto', color: 'text-yellow-600', emoji: '👌' },
  poor: { label: 'Scarso', color: 'text-orange-600', emoji: '⚠️' },
  broken: { label: 'Rotto', color: 'text-red-600', emoji: '❌' }
};

const iconOptions = [
  { name: 'Wrench', icon: Wrench },
  { name: 'Laptop', icon: Laptop },
  { name: 'Bed', icon: Bed },
  { name: 'Utensils', icon: Utensils },
  { name: 'Lightbulb', icon: Lightbulb },
  { name: 'Tent', icon: Tent },
  { name: 'Car', icon: Car },
  { name: 'Droplet', icon: Droplet },
  { name: 'Flame', icon: Flame },
  { name: 'Leaf', icon: Leaf },
  { name: 'Music', icon: Music },
  { name: 'Camera', icon: Camera },
  { name: 'Gamepad2', icon: Gamepad2 },
  { name: 'Bike', icon: Bike },
  { name: 'Waves', icon: Waves }
];

const colorOptions = [
  { value: 'blue', bg: 'bg-blue-500', gradient: 'from-blue-500 to-blue-600' },
  { value: 'green', bg: 'bg-green-500', gradient: 'from-green-500 to-green-600' },
  { value: 'red', bg: 'bg-red-500', gradient: 'from-red-500 to-red-600' },
  { value: 'yellow', bg: 'bg-yellow-500', gradient: 'from-yellow-500 to-yellow-600' },
  { value: 'purple', bg: 'bg-purple-500', gradient: 'from-purple-500 to-purple-600' },
  { value: 'pink', bg: 'bg-pink-500', gradient: 'from-pink-500 to-pink-600' },
  { value: 'orange', bg: 'bg-orange-500', gradient: 'from-orange-500 to-orange-600' },
  { value: 'teal', bg: 'bg-teal-500', gradient: 'from-teal-500 to-teal-600' },
  { value: 'gray', bg: 'bg-gray-500', gradient: 'from-gray-500 to-gray-600' },
  { value: 'indigo', bg: 'bg-indigo-500', gradient: 'from-indigo-500 to-indigo-600' }
];

function getIconComponent(iconName: string) {
  const found = iconOptions.find(i => i.name === iconName);
  return found?.icon || Tag;
}

function getColorGradient(color: string) {
  const found = colorOptions.find((c) => c.value === color);
  return found?.gradient || 'from-gray-500 to-gray-600';
}

// ============================================================================
// MODAL COMPONENT
// ============================================================================

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

function Modal({ isOpen, onClose, title, children }: ModalProps) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose} />
        <div className="relative inline-block w-full max-w-lg p-6 my-8 text-left align-middle bg-white rounded-2xl shadow-xl transform transition-all max-h-[90vh] overflow-hidden flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg transition-colors">
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>
          <div className="overflow-y-auto flex-1">
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function CategoryDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Modal & form states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [assetSearch, setAssetSearch] = useState('');
  const [editForm, setEditForm] = useState({
    name: '',
    icon: 'Wrench',
    color: 'blue'
  });

  const { data, isLoading, error } = useQuery({
    queryKey: ['category', id],
    queryFn: () => api.getCategory(id!),
    refetchOnMount: 'always'
  });

  const updateMutation = useMutation({
    mutationFn: (updateData: any) => api.updateCategory(id!, updateData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['category', id] });
      queryClient.invalidateQueries({ queryKey: ['categories'] });
      setEditModalOpen(false);
    }
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary-200 border-t-primary-600 rounded-full animate-spin"></div>
          <p className="text-gray-500 font-medium">Caricamento categoria...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="bg-red-50 text-red-700 p-6 rounded-2xl border border-red-100">
        <div className="flex items-center gap-3">
          <AlertCircle className="w-5 h-5" />
          <span className="font-medium">Errore: {error?.message || 'Categoria non trovata'}</span>
        </div>
        <Link to="/categories" className="mt-4 btn btn-secondary inline-flex items-center gap-2">
          <ArrowLeft className="w-4 h-4" />
          Torna alle categorie
        </Link>
      </div>
    );
  }

  const { category, assets } = data;
  const hasAssets = assets && assets.length > 0;
  const IconComponent = getIconComponent(category.icon || 'Tag');
  const colorGradient = getColorGradient(category.color || 'gray');

  // Filter assets by search
  const filteredAssets = assets.filter(asset =>
    asset.name.toLowerCase().includes(assetSearch.toLowerCase()) ||
    asset.qrCode.toLowerCase().includes(assetSearch.toLowerCase())
  );

  const handleOpenEditModal = () => {
    setEditForm({
      name: category.name || '',
      icon: category.icon || 'Wrench',
      color: category.color || 'blue'
    });
    setEditModalOpen(true);
  };

  const handleSaveEdit = () => {
    updateMutation.mutate({
      name: editForm.name,
      icon: editForm.icon,
      color: editForm.color
    });
  };

  const SelectedIcon = getIconComponent(editForm.icon);
  const selectedColorGradient = getColorGradient(editForm.color);

  return (
    <div className="max-w-6xl mx-auto space-y-6 animate-fadeIn">
      {/* Header with Category Banner */}
      <div className={`rounded-2xl bg-gradient-to-r ${colorGradient} p-6 shadow-lg`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <Link to="/categories" className="icon-btn bg-white/20 hover:bg-white/30 text-white mt-1">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-4">
              <div className="p-4 bg-white/20 rounded-2xl backdrop-blur-sm">
                <IconComponent className="w-10 h-10 text-white" />
              </div>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-white">{category.name}</h1>
                <p className="text-white/80 mt-1">
                  {hasAssets ? `${assets.length} asset` : 'Nessun asset'}
                </p>
              </div>
            </div>
          </div>
          <button
            onClick={handleOpenEditModal}
            className="btn flex items-center gap-2 bg-white/20 hover:bg-white/30 text-white border-white/30"
          >
            <Edit className="w-4 h-4" />
            <span className="hidden sm:inline">Modifica</span>
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Content - Left Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* Category Info Cards */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Tag className="w-5 h-5 text-gray-400" />
              Informazioni Categoria
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Nome</p>
                <p className="font-semibold text-gray-900">{category.name}</p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Icona</p>
                <div className="flex items-center gap-2">
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${colorGradient}`}>
                    <IconComponent className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-gray-900">{category.icon}</span>
                </div>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Colore</p>
                <div className="flex items-center gap-2">
                  <div className={`w-6 h-6 rounded-lg bg-gradient-to-br ${colorGradient}`}></div>
                  <span className="text-gray-900 capitalize">{category.color}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Assets List */}
          <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Package className="w-5 h-5 text-gray-400" />
                Asset in questa categoria
                {hasAssets && (
                  <span className="text-sm font-normal text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    {assets.length}
                  </span>
                )}
              </h2>
              <Link
                to={`/assets/new?category=${id}`}
                className="btn btn-primary btn-sm"
              >
                <Package className="w-4 h-4" />
                Nuovo Asset
              </Link>
            </div>

            {/* Search */}
            {hasAssets && (
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cerca asset per nome o codice..."
                  value={assetSearch}
                  onChange={(e) => setAssetSearch(e.target.value)}
                  className="input pl-10"
                />
              </div>
            )}

            {!hasAssets ? (
              <div className="text-center py-12 bg-gray-50 rounded-xl">
                <Package className="w-16 h-16 text-gray-200 mx-auto mb-4" />
                <p className="text-gray-600 font-medium">Nessun asset in questa categoria</p>
                <p className="text-gray-400 text-sm mt-1">Gli asset creati con questa categoria appariranno qui</p>
                <Link
                  to={`/assets/new?category=${id}`}
                  className="btn btn-primary mt-4"
                >
                  <Package className="w-4 h-4" />
                  Crea primo asset
                </Link>
              </div>
            ) : filteredAssets.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-xl">
                <Search className="w-12 h-12 text-gray-200 mx-auto mb-3" />
                <p className="text-gray-500">Nessun asset trovato per "{assetSearch}"</p>
                <button
                  onClick={() => setAssetSearch('')}
                  className="btn btn-ghost btn-sm mt-2"
                >
                  <X className="w-4 h-4" />
                  Cancella ricerca
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Codice</th>
                      <th>Nome</th>
                      <th>Stato</th>
                      <th className="hidden sm:table-cell">Ubicazione</th>
                      <th className="hidden sm:table-cell">Condizione</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAssets.map((asset) => {
                      const assetStatus = statusConfig[asset.status] || statusConfig.active;
                      const assetCondition = conditionConfig[asset.condition] || conditionConfig.good;
                      return (
                        <tr
                          key={asset.id}
                          className="clickable group"
                          onClick={() => navigate(`/assets/${asset.id}`)}
                        >
                          <td className="font-mono text-xs text-gray-500">{asset.qrCode}</td>
                          <td className="font-medium text-gray-900">{asset.name}</td>
                          <td>
                            <span className={`badge ${assetStatus.bg} ${assetStatus.text}`}>
                              {assetStatus.label}
                            </span>
                          </td>
                          <td className="hidden sm:table-cell text-sm text-gray-600">
                            {asset.location?.name || '-'}
                          </td>
                          <td className="hidden sm:table-cell">
                            <span className={assetCondition.color}>
                              {assetCondition.emoji} {assetCondition.label}
                            </span>
                          </td>
                          <td>
                            <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-primary-500 transition-colors" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Sidebar - Right Column */}
        <div className="space-y-6">
          {/* Statistics Card */}
          <div className="card bg-gradient-to-br from-gray-50 to-white border border-gray-100">
            <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-4">
              Statistiche
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Totale Asset</span>
                <span className="text-2xl font-bold text-gray-900">{assets.length}</span>
              </div>
              
              {hasAssets && (
                <>
                  <div className="h-px bg-gray-200"></div>
                  
                  {/* Status breakdown */}
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Per Stato</p>
                    <div className="space-y-2">
                      {Object.entries(statusConfig)
                        .map(([status, config]) => {
                          const count = assets.filter(a => a.status === status).length;
                          if (count === 0) return null;
                          return (
                            <div key={status} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <div className={`w-2 h-2 rounded-full bg-gradient-to-r ${config.gradient}`}></div>
                                <span className="text-sm text-gray-600">{config.label}</span>
                              </div>
                              <span className="text-sm font-semibold text-gray-900">{count}</span>
                            </div>
                          );
                        })
                        .filter(Boolean)}
                    </div>
                  </div>

                  <div className="h-px bg-gray-200"></div>

                  {/* Condition breakdown */}
                  <div>
                    <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Per Condizione</p>
                    <div className="space-y-2">
                      {Object.entries(conditionConfig)
                        .map(([condition, config]) => {
                          const count = assets.filter(a => a.condition === condition).length;
                          if (count === 0) return null;
                          return (
                            <div key={condition} className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <span>{config.emoji}</span>
                                <span className="text-sm text-gray-600">{config.label}</span>
                              </div>
                              <span className="text-sm font-semibold text-gray-900">{count}</span>
                            </div>
                          );
                        })
                        .filter(Boolean)}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Azioni Rapide</h2>
            <div className="space-y-2">
              <Link
                to={`/assets/new?category=${id}`}
                className="w-full btn flex items-center justify-center gap-2 bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white border-none shadow-sm"
              >
                <Package className="w-4 h-4" />
                Nuovo Asset
              </Link>
              <button
                onClick={handleOpenEditModal}
                className="w-full btn flex items-center justify-center gap-2 bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 text-gray-700 border-gray-200"
              >
                <Edit className="w-4 h-4" />
                Modifica Categoria
              </button>
            </div>
          </div>

          {/* Warning if has assets */}
          {hasAssets && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-amber-800">Categoria con asset</p>
                  <p className="text-amber-700 mt-1">
                    Questa categoria ha {assets.length} asset associati. Il nome non può essere modificato finché sono presenti asset.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ============================================================================
          EDIT MODAL
          ============================================================================ */}

      <Modal isOpen={editModalOpen} onClose={() => setEditModalOpen(false)} title="Modifica Categoria">
        <div className="space-y-4">
          {/* Preview */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Anteprima</p>
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg bg-gradient-to-br ${selectedColorGradient}`}>
                <SelectedIcon className="w-5 h-5 text-white" />
              </div>
              <span className="font-medium text-gray-900">{editForm.name || 'Nome categoria'}</span>
            </div>
          </div>

          {/* Name - disabled if has assets */}
          <div className="form-group">
            <label className="form-label">
              Nome <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={editForm.name}
              onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
              className="input"
              placeholder="es. Attrezzatura sportiva"
              required
              disabled={hasAssets}
            />
            {hasAssets && (
              <p className="form-hint text-amber-600 mt-1">
                Il nome non può essere modificato perché ci sono asset in questa categoria.
              </p>
            )}
          </div>

          {/* Icon */}
          <div className="form-group">
            <label className="form-label">Icona</label>
            <div className="grid grid-cols-5 gap-2">
              {iconOptions.map((iconOpt) => {
                const IconComp = iconOpt.icon;
                const isSelected = editForm.icon === iconOpt.name;
                return (
                  <button
                    key={iconOpt.name}
                    type="button"
                    onClick={() => setEditForm({ ...editForm, icon: iconOpt.name })}
                    className={`p-2 rounded-lg border transition-all flex items-center justify-center ${
                      isSelected
                        ? 'border-primary-500 bg-primary-50 text-primary-600'
                        : 'border-gray-200 hover:border-gray-300 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    <IconComp className="w-4 h-4" />
                  </button>
                );
              })}
            </div>
          </div>

          {/* Color */}
          <div className="form-group">
            <label className="form-label">Colore</label>
            <div className="flex flex-wrap gap-2">
              {colorOptions.map((colorOption) => (
                <button
                  key={colorOption.value}
                  type="button"
                  onClick={() => setEditForm({ ...editForm, color: colorOption.value })}
                  className={`w-8 h-8 rounded-lg bg-gradient-to-br ${colorOption.gradient} transition-all flex items-center justify-center ${
                    editForm.color === colorOption.value
                      ? 'ring-2 ring-offset-1 ring-primary-500 scale-110'
                      : 'hover:scale-105'
                  }`}
                >
                  {editForm.color === colorOption.value && (
                    <Check className="w-4 h-4 text-white" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {updateMutation.error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm">
              {updateMutation.error.message}
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={() => setEditModalOpen(false)}
              className="flex-1 btn btn-secondary"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={handleSaveEdit}
              disabled={!editForm.name.trim() || updateMutation.isPending}
              className="flex-1 btn btn-primary disabled:opacity-50"
            >
              {updateMutation.isPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  Salvataggio...
                </>
              ) : (
                'Salva'
              )}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
